import { useState, useEffect } from 'react'
import { supabase } from '../../api/supabase'
import { parsePOAWithClaude } from '../../api/claudePOAParser'
import { useAuth } from '../../contexts/AuthContext'

interface EORIStatus {
  eori: string
  status: 'not_found' | 'error' | 'valid_open' | 'valid_expiring' | 'expired' | 'loading' | 'pending'
  valid_until?: string | null
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => { resolve((reader.result as string).split(',')[1]) }
    reader.onerror = reject
  })
}

function StatusBadge({ s }: { s: EORIStatus }) {
  switch (s.status) {
    case 'loading': return <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500">Checking...</span>
    case 'error': return <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Unable to check – please sign in again</span>
    case 'not_found': return <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">Not Found</span>
    case 'valid_open': return <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">Valid – Open-ended</span>
    case 'valid_expiring': return <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700">Expiring Soon ({s.valid_until})</span>
    case 'expired': return <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Expired</span>
    case 'pending': return <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">Pending Review</span>
    default: return null
  }
}

export default function POACheck() {
  const { user } = useAuth()
  const [registeredEoris, setRegisteredEoris] = useState<string[]>([])
  const [extraEoriText, setExtraEoriText] = useState('')
  const [statuses, setStatuses] = useState<EORIStatus[]>([])
  const [uploading, setUploading] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  useEffect(() => {
    if (!user?.email) return
    supabase.from('client_accounts').select('eori_numbers').eq('user_email', user.email).single()
      .then(({ data }) => { if (data?.eori_numbers) setRegisteredEoris(data.eori_numbers as string[]) })
  }, [user?.email])

  const handleCheck = async () => {
    if (!user) return
    const extra = extraEoriText.split('\n').map(s => s.trim()).filter(s => /^(GB|XI)/i.test(s))
    const all = [...new Set([...registeredEoris, ...extra])]
    if (all.length === 0) return
    setStatuses(all.map(eori => ({ eori, status: 'loading' as const })))
    const results = await Promise.all(all.map(async (eori): Promise<EORIStatus> => {
      const { data, error } = await supabase.from('poas').select('valid_until, is_open_ended')
        .eq('eori_number', eori).eq('review_status', 'approved')
        .order('created_at', { ascending: false }).limit(1)
      if (error) return { eori, status: 'error' }
      if (!data || data.length === 0) {
        const { data: p, error: pErr } = await supabase.from('poas').select('id')
          .eq('eori_number', eori).eq('review_status', 'pending_review').limit(1)
        if (pErr) return { eori, status: 'error' }
        return p && p.length > 0 ? { eori, status: 'pending' } : { eori, status: 'not_found' }
      }
      const poa = data[0]
      if (poa.is_open_ended) return { eori, status: 'valid_open' }
      const expiry = poa.valid_until ? new Date(poa.valid_until as string) : null
      if (!expiry) return { eori, status: 'valid_open' }
      const days = Math.floor((expiry.getTime() - Date.now()) / 86400000)
      if (days < 0) return { eori, status: 'expired', valid_until: poa.valid_until as string }
      if (days < 30) return { eori, status: 'valid_expiring', valid_until: poa.valid_until as string }
      return { eori, status: 'valid_open' }
    }))
    setStatuses(results)
  }

  const handleUpload = async (eori: string, file: File) => {
    if (file.type !== 'application/pdf') { setUploadError('Please upload a PDF file'); return }
    setUploading(eori); setUploadError(null)
    try {
      const fileName = `${eori}_${Date.now()}.pdf`
      const { error: sErr } = await supabase.storage.from('pending-poa').upload(fileName, file)
      if (sErr) throw new Error(sErr.message)
      const base64 = await fileToBase64(file)
      const ext = await parsePOAWithClaude(base64, 'Unknown Agent')
      if (!ext.importer_name || ext.confidence < 0.4)
        throw new Error('Could not extract sufficient information. Please upload a clearer, complete POA document.')
      const { error: iErr } = await supabase.from('poas').insert({
        eori_number: eori, importer_name: ext.importer_name, importer_address: ext.importer_address,
        vat_number: ext.vat_number, signatory_name: ext.signatory_name, signed_date: ext.signed_date,
        valid_until: ext.valid_until, is_open_ended: ext.is_open_ended,
        is_uk_local_company: ext.is_uk_local_company, is_pva: ext.is_pva,
        is_cds_approved: ext.is_cds_approved, ai_confidence: ext.confidence,
        source: 'client', review_status: 'pending_review', storage_path: fileName
      })
      if (iErr) throw new Error(iErr.message)
      setStatuses(prev => prev.map(s => s.eori === eori ? { ...s, status: 'pending' as const } : s))
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally { setUploading(null) }
  }

  if (!user) {
    return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 text-center text-sm text-gray-600 dark:text-gray-300">Please sign in to view POA status.</div>
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">POA Status Check</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Agent information is not displayed. For queries contact CY UK Ltd.
        </p>
        {registeredEoris.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-700 dark:text-blue-300">Your registered EORIs: <strong>{registeredEoris.length}</strong></p>
          </div>
        )}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            EORIs to check (one per line, GB.../XI... prefix)
          </label>
          <textarea value={extraEoriText} onChange={e => setExtraEoriText(e.target.value)} rows={3}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="GB123456789000" />
          <button onClick={handleCheck}
            className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors">
            Check Status
          </button>
        </div>
        {uploadError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-sm text-red-700">{uploadError}</div>
        )}
        {statuses.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300">EORI</th>
                  <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300">Status</th>
                  <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300">Upload POA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {statuses.map(s => (
                  <tr key={s.eori}>
                    <td className="px-4 py-3 font-mono text-gray-800 dark:text-gray-200">{s.eori}</td>
                    <td className="px-4 py-3"><StatusBadge s={s} /></td>
                    <td className="px-4 py-3">
                      {(s.status === 'not_found' || s.status === 'expired') && (
                        <label className="cursor-pointer text-blue-600 hover:text-blue-700 text-xs underline">
                          {uploading === s.eori ? 'Uploading...' : 'Upload PDF'}
                          <input type="file" accept="application/pdf" className="hidden"
                            disabled={uploading === s.eori}
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(s.eori, f) }} />
                        </label>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
